import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MoreIcon from '@mui/icons-material/MoreVert';
import PermIdentityIcon from '@mui/icons-material/PermIdentity';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { CssBaseline, Paper, SwipeableDrawer } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { styled, useTheme } from '@mui/material/styles';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import { t } from 'i18next';
import * as React from 'react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { baseUrl } from '../api';
import LangMenu from '../components/LangMenu';
import AppButton from '../components/atoms/AppButton';
import AppModal from '../components/atoms/AppModal';
import OTPModal from '../components/atoms/OTPModal';
import ProfileModal from '../components/atoms/ProfileModal';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { selectCurrentCategory, setCurrentSearch } from '../redux/slices/productList';
import { selectIsLoggedIn, selectUserId, selectUserName } from '../redux/slices/user';
import { postData } from '../redux/thunks/itemsApi';
import { handleSendOtp, handleVerifyOtp } from '../services/appFunctions';
import i18n, { changeAppLanguage } from '../services/localization/i18n';
import { debounce } from '../utils/debounce';
import AppDrawer from './AppDrawer';
import LayoutStyle from './Layout.style';
import { resetParams } from '../redux/slices/params';
import CartButton from '../components/cart/CartButton';
import { featureFlags } from '../config/featureFlags';
import { CurrencySelector } from '../components/CurrencySelector';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { getCountryIcon } from '../utils/commonUtils';
interface Props {
    /**
     * Injected by the documentation to work in an iframe.
     * You won't need it on your project.
     */
    window?: () => Window;
    children: React.ReactElement;
}

function ElevationScroll(props: Props) {
    const { children, window } = props;
    // Note that you normally won't need to set the window ref as useScrollTrigger
    // will default to window.
    // This is only being set here because the demo is in an iframe.
    const trigger = useScrollTrigger({
        disableHysteresis: true,
        threshold: 0,
        target: window ? window() : undefined
    });

    return React.cloneElement(children, {
        elevation: trigger ? 4 : 0,
        style: {
            boxShadow: trigger ? '0px 1px 4px rgba(0, 0, 0, 0.25)' : 'none',
            backgroundColor: trigger ? 'primary.main' : 'topnavbar.main',
            transition: 'all 0.3s ease-in-out',
            position: 'sticky',
            top: 0,
            zIndex: 1100,
            AnimationEffectTiming: 'ease-in-out'
        }
    });
}

interface ITopNavbar {
    colorModeToggle: () => void;
}
const PrimarySearchAppBar: React.FC<ITopNavbar> = ({ colorModeToggle }) => {
    const dispatch = useAppDispatch();
    const navigation = useNavigate();
    const theme = useTheme();
    const autoUrl = `${baseUrl}/Items/GetFilterData`;

    const userId = useAppSelector(selectUserId);
    const userName = useAppSelector(selectUserName);
    const isLoggedIn = useAppSelector(selectIsLoggedIn);

    const currentCategory = useAppSelector(selectCurrentCategory);
    const handleLanguage = (e: React.MouseEvent<HTMLElement>) => setAnchorLang(e.currentTarget);
    const [openRegister, setOpenRegister] = React.useState(false);
    const handleRegister = () => navigation('/login');
    const [anchorLang, setAnchorLang] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorLang);
    const changeLanguage = async (lng: string | undefined) => {
        if (!lng) return;
        await changeAppLanguage(lng);
        setAnchorLang(null);
    };
    const currentPath = window.location.pathname;
    // profile modal functions
    const [anchorElM, setAnchorElM] = React.useState<null | HTMLElement>(null);
    const openM = Boolean(anchorElM);
    const [autoData, setAutoData] = React.useState([]);
    const [search, setSearch] = React.useState('');
    const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [isOtpOpen, setIsOtpOpen] = useState(false);

    const handleClickM = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElM(event.currentTarget);
    };
    const handleCloseM = () => {
        setAnchorElM(null);
    };

    const autoCompleteAbortRef = useRef<AbortController | null>(null);
    const autoCompleteRequestIdRef = useRef(0);

    const getAutoComplete = useCallback(async (text: string) => {
        if (text.length === 0) return;

        autoCompleteAbortRef.current?.abort();
        const controller = new AbortController();
        autoCompleteAbortRef.current = controller;
        const requestId = ++autoCompleteRequestIdRef.current;

        try {
            const response = await postData(autoUrl, { text, lang: i18n.language }, { signal: controller.signal });
            if (requestId !== autoCompleteRequestIdRef.current) return;
            setAutoData(response ?? []);
        } catch (err) {
            if ((err as Error).name === 'AbortError') return;
            if (requestId !== autoCompleteRequestIdRef.current) return;
            setAutoData([]);
        }
    }, [autoUrl]);

    const debouncedAutoComplete = useMemo(() => debounce(getAutoComplete, 300), [getAutoComplete]);

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearch(value);
        if (value.length === 0) {
            debouncedAutoComplete.cancel();
            autoCompleteAbortRef.current?.abort();
            setAutoData([]);
            return;
        }
        debouncedAutoComplete(value);
    };

    // The home feed reloads (from page 1) whenever the current search changes, so no request is made here
    const handleSearch = useCallback(
        (chosenSearch: string) => {
            dispatch(setCurrentSearch(chosenSearch));
            setSearch(chosenSearch);
        },
        [dispatch]
    );

    const handleClearSearch = () => {
        dispatch(setCurrentSearch(''));
        setSearch('');
        setAutoData([]);
    };

    useEffect(() => {
        return () => {
            debouncedAutoComplete.cancel();
            autoCompleteAbortRef.current?.abort();
        };
    }, [debouncedAutoComplete]);

    useEffect(() => {
        if (search === '') {
            setAutoData([]);
        }
    }, [search]);

    useEffect(() => {
        if (currentCategory === '') return;
        setSearch('');
    }, [currentCategory]);

    const toggleDrawer = () => setIsDrawerOpen(!isDrawerOpen);

    const refreshAndGoToHome = () => {
        dispatch(setCurrentSearch(''));
        dispatch(resetParams());
        setSearch('');
        navigation('/');
    };
    const isTermsNAboutus = currentPath === '/term-and-condition' || currentPath === '/about_us';

    const handleToSell = () => {
        if (isLoggedIn) {
            navigation('additem');
        } else {
            handleRegister();
        }
    };
    const handleGoToProfile = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();
        if (isLoggedIn) {
            handleClickM(event);
        } else {
            handleRegister();
        }
    };

    useLayoutEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 767);
        };

        window.addEventListener('resize', handleResize);

        handleResize();

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);
    const handleLoginModalClose = () => setOpenRegister(false);
    const handleOtp = () => {
        setIsOtpOpen((prev) => !prev);
    };
    const handleOptOpen = () => {
        handleLoginModalClose();
        handleOtp();
    };

    const flagIcon = getCountryIcon();
    return (
        <>
            <CssBaseline />
            <ElevationScroll>
                <AppBar position="fixed" sx={LayoutStyle.navbar}>
                    <Container>
                        <Toolbar disableGutters>
                            <Box display={'flex'} sx={LayoutStyle.logoContainer} onClick={refreshAndGoToHome}>
                                <Box component={'img'} onClick={refreshAndGoToHome} src={require('./../assests/images/icon_presign.png')} sx={LayoutStyle.logoStyle} />

                                <Typography variant="h6" noWrap component="div" sx={logoText} onClick={refreshAndGoToHome}>
                                    UZOFFER
                                </Typography>
                            </Box>

                            <Box sx={subconatiner}>
                                <Search
                                    style={{ visibility: window.location.pathname === '/' || window.location.pathname === '/home' ? 'visible' : 'hidden' }}
                                    onKeyDown={(e) => e.key === 'Enter' && handleSearch(search)}
                                >
                                    <StyledInputBase value={search} placeholder={t('web.navbar-search')} inputProps={{ 'aria-label': 'search' }} sx={StyledInputBaseStyle} onChange={handleInput} />
                                    {!!search && (
                                        <IconButton onClick={handleClearSearch} type="submit" sx={clearSearch}>
                                            <CloseIcon sx={closeIcon} />
                                        </IconButton>
                                    )}
                                    <Box component={'button'} sx={btnSearch} onClick={() => handleSearch(search)}>
                                        <SearchRoundedIcon sx={searchRoundedIcon} />
                                    </Box>

                                    {autoData?.length > 0 && (
                                        <Paper
                                            sx={{
                                                width: '100%',
                                                maxWidth: 430,
                                                height: 'auto',
                                                bgcolor: 'white',
                                                position: 'absolute',
                                                top: 45,
                                                p: 3,
                                                animation: 'ease-in-out'
                                            }}
                                            elevation={20}
                                        >
                                            {autoData.map((e) => (
                                                <Typography key={e} component={'h5'} sx={[autoDataTxtStyle, { borderBottom: '1px solid lightgray' }]} onClick={() => handleSearch(e)}>
                                                    {e}
                                                </Typography>
                                            ))}
                                        </Paper>
                                    )}
                                </Search>

                                <Box
                                    sx={{
                                        flexGrow: 1,
                                        display: 'flex',
                                        justifyContent: 'flex-end'
                                    }}
                                >
                                    {isLoggedIn && !isTermsNAboutus && (
                                        <AppButton
                                            onClick={() => {
                                                navigation('/activities');
                                            }}
                                            /* badgeCount={1} */
                                            title={t('web.navbar-trade')}
                                            variant={'text'}
                                            btnStyle={{
                                                bgcolor: 'primary.main',
                                                '&:hover': {
                                                    bgcolor: 'primary.main',
                                                    opacity: 0.8
                                                },
                                                color: 'white'
                                            }}
                                            startIcon={<SyncAltIcon sx={{ width: 18, height: 18 }} />}
                                        />
                                    )}

                                    {!isTermsNAboutus && (
                                        <AppButton startIcon={<AddIcon sx={{ m: 0, p: 0 }} />} title={t('web.navbar-sell')} variant={'text'} onClick={handleToSell} btnStyle={btnStyle} />
                                    )}
                                    <AppButton
                                        onClick={handleGoToProfile}
                                        title={isLoggedIn ? userName : t('web.navbar-profile')}
                                        variant={'text'}
                                        btnStyle={{ color: 'text.primary', bgcolor: 'profilebutton.main' }}
                                        startIcon={
                                            <PermIdentityIcon
                                                sx={{ width: 18, height: 18, color: 'text.primary' }}
                                            /> /* <Box component="img" src={require('./../assests/icons/profile.png')} sx={{ width: 18, height: 18, color: 'text.primary' }} /> */
                                        }
                                        endIcon={isLoggedIn ? <KeyboardArrowDownIcon sx={{ width: 18, height: 18, color: 'text.primary' }} /> : null}
                                    />
                                    {isLoggedIn && !isTermsNAboutus && featureFlags.showCartButton && <CartButton />}

                                    {!isTermsNAboutus && (
                                        <>
                                            <AppButton
                                                variant={'text'}
                                                onClick={handleLanguage}
                                                btnStyle={{ bgcolor: 'transparency' }}
                                                middleIcon={<Box component="img" src={flagIcon} sx={{ width: 22, height: 'auto' }} />}
                                            />
                                            <IconButton onClick={colorModeToggle} sx={{ color: 'text.dark', bgcolor: 'transparency' }}>
                                                {theme.palette.mode === 'dark' ? <LightModeIcon color="action" /> : <DarkModeIcon color="action" />}
                                            </IconButton>
                                            <CurrencySelector />
                                        </>
                                    )}
                                </Box>
                            </Box>

                            <Box sx={{ display: { xs: 'flex', sm: 'none' }, ml: 1 }}>
                                <Search style={{ visibility: window.location.pathname === '/' || window.location.pathname === '/home' ? 'visible' : 'hidden' }}>
                                    <StyledInputBase
                                        value={search}
                                        placeholder={t('web.navbar-search')}
                                        inputProps={{ 'aria-label': 'search' }}
                                        sx={{ flexGrow: 1, zIndex: 2000 }}
                                        onChange={handleInput}
                                    />
                                    {!!search && (
                                        <IconButton
                                            onClick={handleClearSearch}
                                            type="submit"
                                            sx={{
                                                right: 0,
                                                bgcolor: 'transparent',

                                                height: 32,
                                                '&:hover': {
                                                    bgcolor: 'transparent'
                                                },
                                                '&:focus': {
                                                    bgcolor: 'transparent'
                                                },
                                                '&:active': {
                                                    bgcolor: 'transparent'
                                                }
                                            }}
                                        >
                                            <CloseIcon sx={{ width: 16, height: 16, color: 'text.primary' }} />
                                        </IconButton>
                                    )}
                                    <Box component={'button'} sx={{ bgcolor: 'transparent', borderWidth: 0 }} onClick={() => handleSearch(search)}>
                                        <SearchRoundedIcon sx={{ color: '#16CECC', width: 30, height: 30 }} />
                                    </Box>

                                    {autoData.length > 0 && (
                                        <Paper sx={{ width: '100%', maxWidth: 430, height: 'auto', bgcolor: 'white', position: 'absolute', top: 45, p: 3, animation: 'ease-in-out' }} elevation={20}>
                                            {autoData.map((e) => (
                                                <Typography
                                                    key={e}
                                                    component={'h5'}
                                                    sx={{
                                                        bgcolor: 'transparent',
                                                        display: 'block',
                                                        color: '#000',
                                                        fontSize: 14,
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            color: '#16CECC'
                                                        },
                                                        m: 0.5,
                                                        p: 0.25
                                                    }}
                                                    onClick={() => handleSearch(e)}
                                                >
                                                    {e}
                                                </Typography>
                                            ))}
                                        </Paper>
                                    )}
                                </Search>

                                <IconButton size="large" aria-label="show more" aria-controls={'drawer'} aria-haspopup="true" onClick={toggleDrawer}>
                                    <MoreIcon color="action" />
                                </IconButton>
                            </Box>
                        </Toolbar>
                    </Container>
                </AppBar>
            </ElevationScroll>

            {isMobile && (
                <SwipeableDrawer anchor={'right'} open={isDrawerOpen} onClose={toggleDrawer} onOpen={toggleDrawer}>
                    <AppDrawer
                        anchor={'right'}
                        open={isDrawerOpen}
                        onClose={() => setIsDrawerOpen(false)}
                        handleRegister={handleRegister}
                        handleDarkMode={colorModeToggle}
                        isLogined={!!userId}
                        handleOtp={handleOptOpen}
                    />
                </SwipeableDrawer>
            )}

            <AppModal isOpen={openRegister} handleClose={handleLoginModalClose} otpHandler={handleOptOpen} />
            <LangMenu open={open} onClose={changeLanguage} anchorEl={anchorLang} />
            <ProfileModal open={openM} onClose={handleCloseM} anchorEl={anchorElM} handleClick={handleClickM} handleClose={handleCloseM} handleTheme={colorModeToggle} />
            <OTPModal isOpen={isOtpOpen} handleClose={handleOtp} sendOtp={handleSendOtp} verifyOtp={handleVerifyOtp} />
        </>
    );
};

export default React.memo(PrimarySearchAppBar);

const { clearSearch, StyledInputBaseStyle, logoText, btnSearch, btnStyle, autoDataTxtStyle, searchRoundedIcon, closeIcon, subconatiner } = {
    logoText: {
        flexGrow: { xs: 1, md: 0 },
        fontWeight: '700',
        fontSize: { xs: 14, sm: 22 },
        color: 'text.primary'
    },
    subconatiner: { display: { xs: 'none', md: 'flex' }, flexGrow: 1 },
    autoDataTxtStyle: {
        bgcolor: 'transparent',
        display: 'block',
        color: '#000',
        fontSize: 14,
        cursor: 'pointer',
        '&:hover': {
            color: '#16CECC'
        },
        m: 0.5,
        p: 0.25
    },
    btnStyle: {
        minWidth: 150,
        Visibility: window.location.pathname === '/' || window.location.pathname === '/home' ? 'visible' : 'hidden',
        bgcolor: 'primary.main',
        '&:hover': {
            bgcolor: 'primary.main',
            opacity: 0.8
        },
        color: 'white',
        '& .MuiButton-startIcon': { ml: 0, mr: 0 }
    },
    StyledInputBaseStyle: { flexGrow: 1, zIndex: 2000, color: 'secondary.dark' },
    btnSearch: { bgcolor: 'transparent', borderWidth: 0 },
    clearSearch: {
        right: 0,
        bgcolor: 'transparent',

        height: 32,
        '&:hover': {
            bgcolor: 'transparent'
        },
        '&:focus': {
            bgcolor: 'transparent'
        },
        '&:active': {
            bgcolor: 'transparent'
        }
    },
    searchRoundedIcon: { color: '#16CECC', width: 30, height: 30 },
    closeIcon: { width: 16, height: 16, color: '#1A1D1F' }
};

const Search = styled('div')(({ theme }) => ({
    position: 'relative',
    borderRadius: 12,
    backgroundColor: 'secondary.dark' /* alpha(theme.palette.common.white, 1) */,
    height: 48,
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
    border: ' 1px solid #16CECC',

    marginRight: theme.spacing(2),
    padding: theme.spacing(0, 1, 0, 0),
    marginLeft: 0,

    [theme.breakpoints.up('sm')]: {
        marginLeft: theme.spacing(3),
        width: 'auto'
    }
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
    color: 'inherit',
    '& .MuiInputBase-input': {
        padding: theme.spacing(1, 1, 1, 0),
        // vertical padding + font size from searchIcon
        paddingLeft: `calc(1em + ${theme.spacing(-1)})`,
        transition: theme.transitions.create('width')
    }
}));
